<template>
  <div class="w-full bg-slate-50 min-h-screen p-4 md:p-8" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-7xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="mb-8">
        <h1 class="text-2xl font-black text-slate-800 tracking-tight">Conferência</h1>
        <p class="text-sm text-slate-500 font-medium">Gestão e conferência de abastecimentos e faturamento municipal</p>
      </div>

      <!-- ÁREA DE PESQUISA -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm mb-8">
          <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-magnify</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
                  FILTROS DE PESQUISA
              </h2>
          </div>
          <div class="p-8">
              <v-row>
                  <v-col cols="12" sm="12" md="3">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Data Inicial</label>
                    <Date @dateValue="[(filter.startDate = $event)]" label="Data Inicial" />
                  </v-col>
                  <v-col cols="12" sm="12" md="3">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Data Final</label>
                    <Date @dateValue="[(filter.endDate = $event)]" label="Data Final" />
                  </v-col>
                  <v-col cols="12" sm="12" :md="userGroup == 1 ? 3 : 6">
                      <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Município</label>
                      <v-autocomplete
                        v-model="filter.prefecture_id"
                        :items="prefectures"
                        item-text="prefecture_social_name"
                        item-value="prefecture_id"
                        placeholder="SELECIONE O MUNICÍPIO..."
                        solo flat dense hide-details
                        class="custom-select"
                        height="44"
                        clearable
                        @change="onPrefectureChange"
                        no-data-text="Nenhum município encontrado"
                      ></v-autocomplete>
                  </v-col>
                  <v-col v-if="userGroup == 1" cols="12" sm="12" md="3">
                      <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Fornecedor</label>
                      <v-autocomplete
                        v-model="filter.provider_id"
                        :items="providers"
                        item-text="provider_name"
                        item-value="provider_id"
                        placeholder="SELECIONE O FORNECEDOR..."
                        solo flat dense hide-details
                        class="custom-select"
                        height="44"
                        clearable
                        :disabled="!filter.prefecture_id"
                        no-data-text="Nenhum fornecedor encontrado"
                      ></v-autocomplete>
                  </v-col>
              </v-row>
              <div class="flex justify-end mt-8">
                  <button @click="handleFilter" class="bg-orange-500 hover:bg-orange-600 text-white rounded-lg shadow-md shadow-orange-500/10 flex items-center justify-center gap-2 w-fit whitespace-nowrap px-10 h-10 transition-all transform hover:-translate-y-0.5 font-bold text-[11px] uppercase tracking-widest">
                      <v-icon color="white" small class="mr-1" style="font-size: 14px;">mdi-filter-outline</v-icon>
                      FILTRAR RESULTADOS
                  </button>
              </div>
          </div>
      </section>

      <!-- ÁREA DE RESULTADOS (TABS) -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <!-- TABS STRIP -->
          <div class="bg-slate-50 border-b border-slate-200">
             <div class="flex">
                <button 
                  v-for="(t, i) in tabsLabels" :key="i"
                  @click="tab = i; handleTabChange(i)"
                  :class="[
                    'px-8 py-5 text-[10px] font-black uppercase tracking-[0.15em] transition-all border-b-2 outline-none',
                    tab === i ? 'bg-white text-orange-500 border-orange-500' : 'text-slate-400 border-transparent hover:text-slate-600 hover:bg-slate-100/50'
                  ]"
                >
                  <v-icon small class="mr-2" :color="tab === i ? 'orange' : 'slate-400'">{{ tabsIcons[i] }}</v-icon>
                  {{ t }}
                </button>
             </div>
          </div>

          <v-tabs-items v-model="tab" class="bg-white">
            <!-- CONFERÊNCIA -->
            <v-tab-item :key="0">
              <div class="p-8">
                <div class="flex justify-between items-center mb-6">
                    <div class="relative w-80">
                      <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <v-icon small class="text-slate-400">mdi-magnify</v-icon>
                      </div>
                      <input 
                        v-model="searchCoupon"
                        placeholder="PESQUISAR POR CUPOM..."
                        class="bg-slate-50 border border-slate-200 rounded-lg py-2 pl-10 pr-4 text-[10px] text-slate-600 placeholder:text-slate-400 focus:outline-none focus:border-orange-500 transition-all w-full font-bold uppercase tracking-widest"
                      >
                    </div>
                </div>
                
                <v-data-table
                  :loading="loader"
                  :items="conferences"
                  :headers="conferenceHeaders"
                  :search="searchCoupon"
                  hide-default-footer
                  class="custom-table"
                  :page.sync="pageConferencia"
                  @page-count="pageCountConferencia = $event"
                  :items-per-page="itemsPerPage"
                >
                  <template v-slot:item.datehour="{ item }">
                    <span class="text-xs text-slate-600 font-semibold">{{ convertDateTime(item.datehour) }}</span>
                  </template>
                  <template v-slot:item.model_desc="{ item }">
                    <div class="flex flex-col" v-if="item.vehicle_id">
                       <span class="font-bold text-slate-800 text-sm tracking-tight">{{ item.vehicle_board }}</span>
                       <span class="text-[10px] text-slate-400 uppercase font-bold">{{ item.brand_desc }} • {{ item.model_desc }}</span>
                    </div>
                    <span v-else class="text-[10px] text-orange-500 italic font-black uppercase tracking-wider bg-orange-50 px-2 py-1 rounded">Correção Manual</span>
                  </template>
                  <template v-slot:item.supply_liters_stocked="{ item }">
                     <span class="font-black text-slate-700">{{ realMaskII(item.supply_liters_stocked.toFixed(3)) }}</span>
                  </template>
                  <template v-slot:item.total_billing_price="{ item }">
                     <span class="font-black text-slate-900 text-sm">R$ {{ realMask((item.supply_bidding_price * item.supply_liters_stocked).toFixed(2)) }}</span>
                  </template>
                  <template v-slot:item.status_chips="{ item }">
                    <div class="flex gap-1 flex-wrap">
                      <span v-if="item.supply_adjusted" class="px-2 py-0.5 rounded-full text-[9px] bg-blue-50 text-blue-600 font-black uppercase tracking-tighter border border-blue-100">Adjusted</span>
                      <span v-if="item.supply_extra" class="px-2 py-0.5 rounded-full text-[9px] bg-rose-50 text-rose-600 font-black uppercase tracking-tighter border border-rose-100">Bloqueado</span>
                      <span v-if="item.supply_confered" class="px-2 py-0.5 rounded-full text-[9px] bg-emerald-50 text-emerald-600 font-black uppercase tracking-tighter border border-emerald-100">Conferido</span>
                      <span v-if="item.supply_billed" class="px-2 py-0.5 rounded-full text-[9px] bg-indigo-50 text-indigo-600 font-black uppercase tracking-tighter border border-indigo-100">Faturado</span>
                    </div>
                  </template>
                  <template v-slot:item.action="{ item }">
                    <div class="flex items-center justify-end gap-2">
                      <button v-if="!item.supply_confered" @click="getSupplyByUpdate(item.supply_id)" class="p-2 rounded-lg text-slate-400 hover:bg-orange-50 hover:text-orange-500 transition-all">
                        <v-icon small color="currentColor">mdi-pencil</v-icon>
                      </button>
                      <button v-if="!item.supply_confered" @click="checkSupply(item.supply_id)" class="p-2 rounded-lg text-slate-400 hover:bg-emerald-50 hover:text-emerald-500 transition-all">
                        <v-icon small color="currentColor">mdi-check-bold</v-icon>
                      </button>
                      <v-icon v-if="item.supply_confered" color="emerald" small>mdi-check-decagram</v-icon>
                    </div>
                  </template>
                </v-data-table>

                <!-- PAGINATION CONFERENCIA -->
                <div class="bg-slate-50 px-6 py-4 border border-slate-200 flex flex-column md:flex-row justify-between items-center gap-4 text-[10px] text-slate-500 font-bold uppercase tracking-widest w-full mt-6 rounded-xl">
                    <span>Exibindo {{ paginationStartConferencia }} de {{ conferences.length }} registros</span>
                    <div class="flex gap-1">
                        <button @click="pageConferencia > 1 ? pageConferencia-- : null" :disabled="pageConferencia === 1" class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 transition-colors">Anterior</button>
                        <button v-for="p in visiblePagesConferencia" :key="p" @click="pageConferencia = p" :class="p === pageConferencia ? 'bg-slate-700 text-white border-slate-700 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-100'" class="px-3 py-1.5 border rounded-md transition-colors font-bold">{{ p }}</button>
                        <button @click="pageConferencia < pageCountConferencia ? pageConferencia++ : null" :disabled="pageConferencia === pageCountConferencia" class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 transition-colors">Próximo</button>
                    </div>
                </div>

                <div class="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                   <div class="bg-slate-50 p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
                      <div>
                        <span class="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-1">TOTAL DO DIA</span>
                        <span class="text-2xl font-black text-slate-700">R$ {{ totals.day_value ? realMask(totals.day_value.toFixed(2)) : '0,00' }}</span>
                      </div>
                      <v-icon size="40" color="slate-200" style="opacity: 0.3">mdi-calendar-check</v-icon>
                   </div>
                   <div class="bg-orange-50/50 p-6 rounded-2xl border border-orange-100 shadow-sm flex items-center justify-between">
                      <div>
                        <span class="text-[10px] font-black text-orange-400 uppercase tracking-[0.2em] block mb-1">TOTAL LICITADO</span>
                        <span class="text-2xl font-black text-orange-600">R$ {{ totals.bidding_total ? realMask(totals.bidding_total.toFixed(2)) : '0,00' }}</span>
                      </div>
                      <v-icon size="40" color="orange-100" style="opacity:0.5">mdi-currency-usd</v-icon>
                   </div>
                </div>
              </div>
            </v-tab-item>

            <!-- FATURAMENTO -->
            <v-tab-item :key="1">
              <div class="p-8">
                <v-data-table
                  :loading="loader"
                  :items="billings"
                  :headers="billingHeaders"
                  hide-default-footer
                  class="custom-table"
                  :page.sync="pageFaturamento"
                  @page-count="pageCountFaturamento = $event"
                  :items-per-page="itemsPerPage"
                >
                  <template v-slot:item.bill_start_date="{ item }">
                    <span class="text-xs text-slate-600 font-bold uppercase tracking-tight">De {{ convertDate(item.bill_start_date) }} até {{ convertDate(item.bill_end_date) }}</span>
                  </template>
                  <template v-slot:item.sectors="{ item }">
                    <div class="flex flex-col">
                       <span class="font-bold text-slate-700 text-xs uppercase">{{ item.sec_name }}</span>
                       <span class="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">{{ item.subsec_desc || 'GERAL' }}</span>
                    </div>
                  </template>
                  <template v-slot:item.bill_total_value="{ item }">
                    <span class="font-black text-slate-800">R$ {{ realMask(item.bill_total_value.toFixed(2)) }}</span>
                  </template>
                  <template v-slot:item.bill_total_updated="{ item }">
                    <span class="text-orange-600 font-black">R$ {{ realMask(item.bill_total_updated.toFixed(2)) }}</span>
                  </template>
                  <template v-slot:item.action="{ item }">
                    <div class="flex justify-end pr-2">
                       <button @click="printBillingReport(item)" class="p-2 rounded-lg text-slate-400 hover:bg-slate-100 transition-all">
                         <v-icon size="18" color="currentColor">mdi-printer-outline</v-icon>
                       </button>
                    </div>
                  </template>
                </v-data-table>

                <!-- PAGINATION FATURAMENTO -->
                <div class="bg-slate-50 px-6 py-4 border border-slate-200 flex flex-column md:flex-row justify-between items-center gap-4 text-[10px] text-slate-500 font-bold uppercase tracking-widest w-full mt-6 rounded-xl">
                    <span>Exibindo {{ paginationStartFaturamento }} de {{ billings.length }} registros</span>
                    <div class="flex gap-1">
                        <button @click="pageFaturamento > 1 ? pageFaturamento-- : null" :disabled="pageFaturamento === 1" class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 transition-colors">Anterior</button>
                        <button v-for="p in visiblePagesFaturamento" :key="p" @click="pageFaturamento = p" :class="p === pageFaturamento ? 'bg-slate-700 text-white border-slate-700 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-100'" class="px-3 py-1.5 border rounded-md transition-colors font-bold">{{ p }}</button>
                        <button @click="pageFaturamento < pageCountFaturamento ? pageFaturamento++ : null" :disabled="pageFaturamento === pageCountFaturamento" class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 transition-colors">Próximo</button>
                    </div>
                </div>

                <div class="mt-8 bg-slate-800 p-8 rounded-2xl shadow-xl flex items-center justify-between overflow-hidden relative">
                   <div class="absolute right-0 top-0 h-full w-32 bg-orange-500/10 skew-x-12 transform translate-x-16"></div>
                   <div>
                      <span class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] block mb-2">VALOR ACUMULADO NO PERÍODO</span>
                      <span class="text-3xl font-black text-white italic">R$ {{ totals.billing_price ? realMask(totals.billing_price.toFixed(2)) : '0,00' }}</span>
                   </div>
                   <v-icon size="60" color="white" style="opacity: 0.1">mdi-receipt-text-check</v-icon>
                </div>
              </div>
            </v-tab-item>

            <!-- ADICIONAR ABASTECIMENTO -->
            <v-tab-item :key="2">
              <div class="p-8">
                 <v-form ref="form" v-model="valid" lazy-validation>
                    <div class="grid grid-cols-1 md:grid-cols-12 gap-8">
                       <div class="md:col-span-4">
                          <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Data e Hora</label>
                          <v-text-field
                            v-model="data.datehour"
                            v-mask="'00/00/0000 00:00'"
                            placeholder="DD/MM/AAAA HH:MM"
                            solo flat dense hide-details
                            class="custom-field-form"
                            :rules="fieldRules"
                          />
                       </div>
                       <div class="md:col-span-8">
                          <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Município</label>
                          <v-autocomplete
                            v-model="data.prefecture_id"
                            :items="prefectures"
                            item-text="prefecture_social_name"
                            item-value="prefecture_id"
                            placeholder="SELECIONE O MUNICÍPIO..."
                            solo flat dense hide-details
                            class="custom-field-form"
                            :rules="fieldRules"
                            @change="[getVehicles(data.prefecture_id), getItems(data.prefecture_id), getDrivers(data.prefecture_id), getSecretariats(data.prefecture_id)]"
                          />
                       </div>
                       <div class="md:col-span-6">
                          <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Veículo</label>
                          <v-autocomplete
                            v-model="data.vehicle_id"
                            :items="vehicles"
                            item-text="vehicle_board"
                            item-value="vehicle_id"
                            placeholder="SELECIONE O VEÍCULO..."
                            solo flat dense hide-details
                            class="custom-field-form"
                          >
                            <template slot="selection" slot-scope="{ item }">{{ item.vehicle_board }} — {{ item.brand_desc }}</template>
                            <template slot="item" slot-scope="{ item }">
                               <div class="flex flex-col py-1">
                                  <span class="font-bold text-slate-700">{{ item.vehicle_board }}</span>
                                  <span class="text-[10px] text-slate-400 uppercase font-black">{{ item.brand_desc }} • {{ item.model_desc }}</span>
                               </div>
                            </template>
                          </v-autocomplete>
                       </div>
                       <div class="md:col-span-6">
                          <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Motorista</label>
                          <v-autocomplete
                            v-model="data.driver_id"
                            :items="drivers"
                            item-text="driver_name"
                            item-value="driver_id"
                            placeholder="SELECIONE O MOTORISTA..."
                            solo flat dense hide-details
                            class="custom-field-form"
                            :rules="fieldRules"
                          />
                       </div>
                       <div class="md:col-span-6">
                          <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Secretaria</label>
                          <v-autocomplete
                            v-model="data.sec_id"
                            :items="secretariats"
                            item-text="sec_name"
                            item-value="sec_id"
                            placeholder="SELECIONE A SECRETARIA..."
                            solo flat dense hide-details
                            class="custom-field-form"
                            :rules="fieldRules"
                            @change="getSubsectors(data.sec_id)"
                          />
                       </div>
                       <div class="md:col-span-6">
                          <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Sub-setor</label>
                          <v-autocomplete
                            v-model="data.subsec_id"
                            :items="subsectors"
                            item-text="subsec_desc"
                            item-value="subsec_id"
                            placeholder="SELECIONE O SUB-SETOR..."
                            solo flat dense hide-details
                            class="custom-field-form"
                          />
                       </div>
                       <div class="md:col-span-4">
                          <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Item Licitado</label>
                          <v-autocomplete
                            v-model="data.item_id"
                            :items="items"
                            item-text="item_description_desc"
                            item-value="item_id"
                            placeholder="SELECIONE O ITEM..."
                            solo flat dense hide-details
                            class="custom-field-form"
                            :rules="fieldRules"
                            @change="loadBidding(data.item_id)"
                          >
                            <template v-slot:selection="{ item }">
                              {{ item.item_description_desc }} — R$ {{ item.item_price ? realMaskII(item.item_price.toFixed(3)) : '0,00' }}
                            </template>
                          </v-autocomplete>
                       </div>
                       <div class="md:col-span-3">
                          <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Preço do Dia</label>
                          <v-text-field
                            v-model="data.supply_price_day"
                            v-mask-decimal.br="3"
                            @change="calculateTotals"
                            solo flat dense hide-details
                            class="custom-field-form"
                            :rules="fieldRules"
                          />
                       </div>
                       <div class="md:col-span-3">
                          <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Quantidade (Litros)</label>
                          <v-text-field
                            v-model="data.supply_liters_stocked"
                            v-mask-decimal.br="3"
                            @change="calculateTotals"
                            solo flat dense hide-details
                            class="custom-field-form"
                            :rules="fieldRules"
                          />
                       </div>
                       <div class="md:col-span-6 bg-slate-900 rounded-xl p-4 flex flex-col justify-center border-l-4 border-orange-500 mb-6">
                          <label class="block text-[9px] font-black text-slate-500 uppercase tracking-[0.2em] mb-1">VALOR TOTAL DO CUPOM</label>
                          <span class="text-xl font-black text-white tracking-tight">R$ {{ realMask(Number(data.supply_value || 0).toFixed(2)) }}</span>
                       </div>

                       <!-- NOVOS CAMPOS DO PRINT -->
                       <div class="md:col-span-3">
                          <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Preço Licitado</label>
                          <div class="bg-slate-50 border border-dotted border-slate-300 rounded-lg px-4 py-3 text-sm text-slate-400 font-bold h-[48px] flex items-center">
                             R$ {{ realMaskII(data.supply_bidding_price || 0) }}
                          </div>
                       </div>
                       <div class="md:col-span-3">
                          <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Total Licitado</label>
                          <div class="bg-slate-50 border border-dotted border-slate-300 rounded-lg px-4 py-3 text-sm text-slate-400 font-bold h-[48px] flex items-center">
                             R$ {{ realMask(Number(data.total_licitado || 0).toFixed(2)) }}
                          </div>
                       </div>
                       <div class="md:col-span-6">
                          <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Km Atual</label>
                          <v-text-field
                            v-model="data.supply_km"
                            v-mask-number
                            solo flat dense hide-details
                            class="custom-field-form"
                            placeholder="INFORME O KM..."
                          />
                       </div>

                       <div class="md:col-span-12 mt-4">
                          <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Número do Cupom Fiscal</label>
                          <v-text-field
                            v-model="data.supply_coupon"
                            solo flat dense hide-details
                            class="custom-field-form"
                            :rules="fieldRules"
                            placeholder="Nº DO CUPOM..."
                          />
                       </div>
                       <div class="md:col-span-12">
                          <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Observação / Justificativa</label>
                          <textarea
                            v-model="data.justificativa"
                            placeholder="DESCREVA A JUSTIFICATIVA SE NECESSÁRIO..."
                            class="w-full h-24 bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-700 font-bold focus:outline-none focus:border-orange-500 transition-all resize-none"
                          ></textarea>
                       </div>
                       <div class="md:col-span-6 flex items-center pt-6">
                          <div class="flex items-center gap-3 p-4 bg-orange-50 rounded-xl border border-orange-100 w-full group cursor-pointer" @click="data.supply_extra = !data.supply_extra">
                             <div :class="['w-6 h-6 rounded-md flex items-center justify-center transition-all border-2', data.supply_extra ? 'bg-orange-500 border-orange-500' : 'bg-white border-orange-200']">
                                <v-icon v-if="data.supply_extra" color="white" small>mdi-check-bold</v-icon>
                             </div>
                             <span class="text-[10px] font-black text-orange-600 uppercase tracking-widest">Marcar como abastecimento de correção</span>
                          </div>
                       </div>
                    </div>

                    <div class="flex justify-end mt-12">
                       <button @click="validate" class="bg-slate-800 hover:bg-slate-900 text-white rounded-xl shadow-2xl flex items-center justify-center gap-3 px-12 h-14 transition-all transform hover:-translate-y-1 active:scale-95 group">
                          <v-icon color="orange" size="20">mdi-shield-check</v-icon>
                          <span class="font-black uppercase tracking-[0.2em] text-[11px]">SALVAR ABASTECIMENTO</span>
                       </button>
                    </div>
                 </v-form>
              </div>
            </v-tab-item>

            <!-- ABASTECIMENTOS 2 -->
            <v-tab-item :key="3">
              <div class="p-8">
                <v-data-table
                  :headers="abastecimentoheaders"
                  :items="abastecimentoData"
                  hide-default-footer
                  :loading="loader"
                  class="custom-table"
                  :page.sync="pageAbast2"
                  @page-count="pageCountAbast2 = $event"
                  :items-per-page="itemsPerPage"
                >
                  <template v-slot:item.datehour="{ item }">
                    <span class="text-xs text-slate-600 font-semibold">{{ convertDateTime(item.datehour) }}</span>
                  </template>
                  <template v-slot:item.model_desc="{ item }">
                    <div class="flex flex-col" v-if="item.vehicle_id">
                       <span class="font-bold text-slate-800 text-sm tracking-tight">{{ item.vehicle_board }}</span>
                       <span class="text-[9px] text-slate-400 font-extrabold uppercase tracking-tight">{{ item.brand_desc }} • {{ item.model_desc }}</span>
                    </div>
                    <span v-else class="text-[10px] text-slate-400 italic font-bold uppercase">NÃO VINCULADO</span>
                  </template>
                  <template v-slot:item.supply_liters_stocked="{ item }">
                    <span class="font-black text-slate-700">{{ realMaskII(item.supply_liters_stocked?.toFixed(3) ?? "0") }}</span>
                  </template>
                  <template v-slot:item.supply_value="{ item }">
                    <span class="font-black text-slate-950">R$ {{ realMask(item.supply_value?.toFixed(2) ?? "0") }}</span>
                  </template>
                  <template v-slot:item.supply_status="{ item }">
                    <span :class="['px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-[0.1em] border', item.supply_status === 'CONCLUIDO' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-orange-50 text-orange-700 border-orange-100']">
                      {{ item.supply_status }}
                    </span>
                  </template>
                </v-data-table>

                <!-- PAGINATION ABAST2 -->
                <div class="bg-slate-50 px-6 py-4 border border-slate-200 flex flex-column md:flex-row justify-between items-center gap-4 text-[10px] text-slate-500 font-bold uppercase tracking-widest w-full mt-6 rounded-xl">
                    <span>Exibindo {{ paginationStartAbast2 }} de {{ abastecimentoData.length }} registros</span>
                    <div class="flex gap-1">
                        <button @click="pageAbast2 > 1 ? pageAbast2-- : null" :disabled="pageAbast2 === 1" class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 transition-colors">Anterior</button>
                        <button v-for="p in visiblePagesAbast2" :key="p" @click="pageAbast2 = p" :class="p === pageAbast2 ? 'bg-slate-700 text-white border-slate-700 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-100'" class="px-3 py-1.5 border rounded-md transition-colors font-bold">{{ p }}</button>
                        <button @click="pageAbast2 < pageCountAbast2 ? pageAbast2++ : null" :disabled="pageAbast2 === pageCountAbast2" class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 transition-colors">Próximo</button>
                    </div>
                </div>
              </div>
            </v-tab-item>
          </v-tabs-items>
      </section>
    </div>

    <!-- DIALOG EDIT -->
    <v-dialog v-model="showForm" width="600" persistent>
      <v-card class="rounded-2xl overflow-hidden border border-slate-200 shadow-2xl">
        <div class="px-8 py-5 flex items-center justify-between" style="background-color: #333333;">
           <div class="flex items-center gap-3">
              <v-icon color="orange" small>mdi-pencil-box-outline</v-icon>
              <h3 class="text-[11px] font-black text-white uppercase tracking-[0.2em]">Alterar Abastecimento</h3>
           </div>
           <button @click="showForm = false" class="text-white hover:text-orange-400 transition-colors bg-white/10 p-1.5 rounded-lg">
             <v-icon size="18" color="currentColor">mdi-close</v-icon>
           </button>
        </div>
        <v-card-text class="py-10 px-8">
          <v-form ref="editForm">
            <v-row>
              <v-col cols="12">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Data e Hora do Registro</label>
                <input 
                  v-model="dataAdjust.datehour"
                  v-mask="'00/00/0000 00:00'"
                  class="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 font-bold focus:outline-none focus:border-orange-500 transition-all font-mono"
                >
              </v-col>
              <v-col cols="12" md="6">
                 <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Quantidade (Litros)</label>
                 <v-text-field
                   v-model="dataAdjust.supply_liters_stocked"
                   v-mask-decimal.br="3"
                   @change="calculateSubTotals"
                   solo flat dense hide-details
                   class="custom-field-form"
                 />
              </v-col>
              <v-col cols="12" md="6">
                 <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Preço Unitário (Dia)</label>
                 <v-text-field
                   v-model="dataAdjust.supply_price_day"
                   v-mask-decimal.br="3"
                   @change="calculateSubTotals"
                   solo flat dense hide-details
                   class="custom-field-form"
                 />
              </v-col>
              <v-col cols="12" md="6">
                 <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Valor Total</label>
                 <div class="bg-slate-900 p-3 rounded-lg border-l-4 border-orange-500">
                    <span class="font-black text-white text-lg">R$ {{ realMask(Number(dataAdjust.supply_value || 0).toFixed(2)) }}</span>
                 </div>
              </v-col>
              <v-col cols="12" md="6">
                 <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">NF-e / Cupom</label>
                 <v-text-field
                   v-model="dataAdjust.supply_coupon"
                   solo flat dense hide-details
                   class="custom-field-form"
                   placeholder="Nº DO DOCUMENTO"
                 />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <div class="px-8 py-5 bg-slate-50 flex justify-end gap-3 border-t border-slate-100">
           <button @click="showForm = false" class="px-6 py-2 text-[10px] font-black text-slate-400 hover:text-slate-600 transition-colors uppercase tracking-[0.2em]">Cancelar</button>
           <button @click="ajust" :disabled="loader" class="bg-slate-800 hover:bg-slate-950 text-white font-black text-[10px] uppercase tracking-[0.2em] px-10 py-3.5 rounded-xl shadow-lg flex items-center gap-2 group transition-all">
              <v-icon v-if="loader" small class="animate-spin">mdi-loading</v-icon>
              <span>EFETUAR ALTERAÇÕES</span>
           </button>
        </div>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snack.show"
      :color="snack.color"
      timeout="4000"
      top
      elevation="24"
      class="custom-snack"
    >
      <div class="flex items-center gap-3">
        <v-icon color="white" small>{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
        <span class="font-bold text-xs uppercase tracking-wider">{{ snack.message }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false" class="bg-white/10 ml-4 rounded">
          <v-icon size="16">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Helpers from "@/mixins/helpers.js";
import Date from "@/components/InputDate.vue";

export default {
  props: ["id"],
  mixins: [Helpers],
  components: { Date },
  data: () => ({
    userGroup: null,
    showForm: false,
    tab: 0,
    loader: false,
    valid: false,
    tabsLabels: ["Conferência", "Faturamento", "Adicionar Abastecimento", "Abastecimentos 2"],
    tabsIcons: ["mdi-calendar-check", "mdi-receipt-text", "mdi-plus-circle-outline", "mdi-history"],
    
    itemsPerPage: 10,
    pageConferencia: 1,
    pageCountConferencia: 0,
    pageFaturamento: 1,
    pageCountFaturamento: 0,
    pageAbast2: 1,
    pageCountAbast2: 0,

    dataAdjust: {
      supply_id: null,
      supply_liters_stocked: 0,
      supply_price_day: 0,
      supply_coupon: null,
      supply_value: 0,
    },
    totals: {
      bidding_total: 0,
      day_value: 0,
      billing_price: 0,
    },
    subsectors: [],
    secretariats: [],
    drivers: [],
    providers: [],
    prefectures: [],
    vehicles: [],
    items: [],
    conferences: [],
    billings: [],
    abastecimentoData: [],
    searchCoupon: "",
    
    filter: {
      endDate: "",
      startDate: "",
      provider_id: null,
      prefecture_id: null,
    },
    
    data: {
      supply_id: null,
      supply_price_day: 0,
      datehour: "",
      supply_bidding_price: 0,
      supply_adjusted: false,
      supply_liters_stocked: 0,
      supply_billed: false,
      supply_extra: false,
      supply_status: "CONCLUIDO",
      item_id: null,
      vehicle_id: null,
      driver_id: null,
      sec_id: null,
      subsec_id: null,
      justificativa: null,
      supply_km: null,
      total_licitado: 0,
      provider_id: null,
      prefecture_id: null,
    },

    fieldRules: [(v) => !!v || "Este campo é requerido"],
    snack: {
      show: false,
      message: "",
      color: "success",
    },

    conferenceHeaders: [
      { text: "Data/Hora", value: "datehour", align: 'start', class: "text-uppercase caption font-weight-black" },
      { text: "Veículo", value: "model_desc", class: "text-uppercase caption font-weight-black" },
      { text: "Tipo", value: "item_description_desc", class: "text-uppercase caption font-weight-black" },
      { text: "Qtde (L)", value: "supply_liters_stocked", align: 'center', class: "text-uppercase caption font-weight-black" },
      { text: "T. Licitado", value: "total_billing_price", align: 'center', class: "text-uppercase caption font-weight-black" },
      { text: "Situação", value: "status_chips", sortable: false, class: "text-uppercase caption font-weight-black" },
      { text: "Cupom", value: "supply_coupon", align: 'center', class: "text-uppercase caption font-weight-black" },
      { text: "Ações", align: "right", value: "action", sortable: false, class: "text-uppercase caption font-weight-black" },
    ],
    billingHeaders: [
      { text: "Período", value: "bill_start_date", class: "text-uppercase caption font-weight-black" },
      { text: "Setores", value: "sectors", class: "text-uppercase caption font-weight-black" },
      { text: "Nº Documentos", value: "bill_interest", align: 'center', class: "text-uppercase caption font-weight-black" },
      { text: "NF-e Emitida", value: "bill_total_value", align: 'center', class: "text-uppercase caption font-weight-black" },
      { text: "Vl. Atualizado", value: "bill_total_updated", align: 'center', class: "text-uppercase caption font-weight-black" },
      { text: "Relatório", align: "right", value: "action", sortable: false, class: "text-uppercase caption font-weight-black" },
    ],
    abastecimentoheaders: [
      { text: "Data e Registro", value: "datehour", align: "center", class: "text-uppercase caption font-weight-black" },
      { text: "Frota Vinculada", value: "model_desc", class: "text-uppercase caption font-weight-black" },
      { text: "Condutor", value: "driver_name", class: "text-uppercase caption font-weight-black" },
      { text: "Ítem / Produto", value: "item_description_desc", class: "text-uppercase caption font-weight-black" },
      { text: "Status", value: "supply_status", align: "center", class: "text-uppercase caption font-weight-black" },
      { text: "Qtde", value: "supply_liters_stocked", align: "center", class: "text-uppercase caption font-weight-black" },
      { text: "Valor Total", value: "supply_value", align: "center", class: "text-uppercase caption font-weight-black" },
    ],
  }),

  computed: {
    paginationStartConferencia() { return (this.pageConferencia - 1) * this.itemsPerPage + 1; },
    visiblePagesConferencia() {
      let pages = [];
      const totalVisible = 5;
      let start = Math.max(1, this.pageConferencia - Math.floor(totalVisible / 2));
      let end = Math.min(this.pageCountConferencia, start + totalVisible - 1);
      if (end - start + 1 < totalVisible) start = Math.max(1, end - totalVisible + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },
    paginationStartFaturamento() { return (this.pageFaturamento - 1) * this.itemsPerPage + 1; },
    visiblePagesFaturamento() {
      let pages = [];
      const totalVisible = 5;
      let start = Math.max(1, this.pageFaturamento - Math.floor(totalVisible / 2));
      let end = Math.min(this.pageCountFaturamento, start + totalVisible - 1);
      if (end - start + 1 < totalVisible) start = Math.max(1, end - totalVisible + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },
    paginationStartAbast2() { return (this.pageAbast2 - 1) * this.itemsPerPage + 1; },
    visiblePagesAbast2() {
      let pages = [];
      const totalVisible = 5;
      let start = Math.max(1, this.pageAbast2 - Math.floor(totalVisible / 2));
      let end = Math.min(this.pageCountAbast2, start + totalVisible - 1);
      if (end - start + 1 < totalVisible) start = Math.max(1, end - totalVisible + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },
  },

  created() {
    this.Helpers;
    const userData = this.$store.getters.getUserData;
    this.userGroup = userData.group_id;

    if (this.userGroup == 1) {
       this.$http.get("/prefectures").then((r) => (this.prefectures = r.data));
    } else {
       this.filter.provider_id = userData.table_id;
       this.$http
         .get(`/supplies/prefectures/conference/${userData.table_id}`)
         .then((r) => (this.prefectures = r.data));
    }

    if (this.id) {
      this.loadSingleSupply(this.id);
    }
  },

  methods: {
    onPrefectureChange(val) {
      if (this.userGroup == 1) {
        this.filter.prefecture_id = val;
        this.getProviders();
      }
    },

    getProviders() {
      if (!this.filter.prefecture_id) {
        this.providers = [];
        return;
      }
      this.$http
        .get(`/providers/prefecture/${this.filter.prefecture_id}`)
        .then((r) => {
          this.providers = r.data;
          // Fallback caso não encontre nada pelo biddings_items
          if (this.providers.length === 0) {
            this.$http.get(`/readjust/providers/${this.filter.prefecture_id}`).then(r2 => {
              this.providers = r2.data;
            });
          }
        })
        .catch((e) => {
          this.snack.show = true;
          this.snack.message = "Erro ao carregar fornecedores: " + (e.response ? e.response.data : e.message);
          this.snack.color = "error";
        });
    },

    handleFilter() {
      if (this.tab === 0) this.search();
      if (this.tab === 1) this.searchBillings();
      if (this.tab === 3) this.searchAbastecimento();
    },

    handleTabChange(i) {
      if (i === 0) this.search();
      if (i === 1) this.searchBillings();
      if (i === 3) this.searchAbastecimento();
    },

    loadSingleSupply(id) {
       this.$store.dispatch("setLoader", true);
       this.$http.get(`supplies/single/${id}`).then((r) => {
         this.data = r.data[0];
         this.setTotalValues(this.data);
         this.$http.get(`/supplies/providers/${r.data[0].provider_id}`).then((i) => {
             this.items = i.data;
         });
         this.$store.dispatch("setLoader", false);
       });
    },

    search() {
      if (!this.filter.prefecture_id || (this.userGroup == 1 && !this.filter.provider_id)) {
        this.showSnack("Selecione o município e fornecedor", "warning");
        return;
      }
      this.totals.day_value = 0;
      this.totals.bidding_total = 0;
      this.loader = true;
      this.$http.post("/supplies/repairvalues").then(() => {
        this.$http
          .post("/supplies/conferences", this.filter)
          .then((r) => {
            this.conferences = r.data;
            this.conferences.forEach((supply) => {
              const liters = supply.supply_liters_stocked || 0;
              const bidding_price = supply.supply_bidding_price || 0;
              const supply_price_day = supply.supply_price_day || 0;
              this.totals.day_value += parseFloat(supply_price_day) * parseFloat(liters);
              this.totals.bidding_total += parseFloat(bidding_price) * parseFloat(liters);
            });
            this.loader = false;
          })
          .catch((e) => {
            this.showSnack(e.message, "error");
            this.loader = false;
          });
      });
    },

    searchBillings() {
      if (!this.filter.prefecture_id || (this.userGroup == 1 && !this.filter.provider_id)) return;
      this.totals.billing_price = 0;
      this.loader = true;
      this.$http
        .post(`/billings/provider`, this.filter)
        .then((r) => {
          this.billings = r.data;
          this.billings.forEach((b) => {
            this.totals.billing_price += parseFloat(b.bill_total_value);
          });
          this.loader = false;
        })
        .catch((e) => {
          this.showSnack(e.message, "error");
          this.loader = false;
        });
    },

    searchAbastecimento() {
      if (!this.filter.prefecture_id || (this.userGroup == 1 && !this.filter.provider_id)) return;
      this.loader = true;
      this.$http
        .put("/supplies/billings", {
          ...this.filter,
          all_status: true,
          supply_extra: true,
          is_autorized: true,
          allow_vehicle_null: true,
        })
        .then((r) => {
          this.abastecimentoData = r.data;
          this.loader = false;
        })
        .catch((e) => {
          this.showSnack(e.message, "error");
          this.loader = false;
        });
    },

    getVehicles(prefecture_id) {
      if (!prefecture_id) return;
      this.$http.get(`/vehicles/prefectures/${prefecture_id}`).then((r) => {
        this.vehicles = r.data;
      });
    },

    getDrivers(prefecture_id) {
      if (!prefecture_id) return;
      this.$http.get(`/supplies/drivers/${prefecture_id}`).then((r) => {
        this.drivers = r.data;
      });
    },

    getSecretariats(prefecture_id) {
      if (!prefecture_id) return;
      this.$http.get(`/prefectures/secretariats/${prefecture_id}`).then((r) => {
        this.secretariats = r.data;
      });
    },

    getSubsectors(sec_id) {
      if (!sec_id) return;
      this.$http.get(`/prefectures/subsectors/${sec_id}`).then((r) => {
        this.subsectors = r.data;
      });
    },

    getItems(prefecture_id) {
      if (!prefecture_id) return;
      const providerId = this.userGroup == 1 ? this.filter.provider_id : this.$store.getters.getUserData.table_id;
      if (!providerId) return;
      this.$http.get(`/biddings/items/${prefecture_id}/${providerId}`).then((i) => (this.items = i.data));
    },

    loadBidding(item_id) {
      const item = this.items.find((v) => v.item_id == item_id);
      if (item) {
        this.data.supply_bidding_price = item.item_billing_price.toFixed(3);
        this.calculateTotals();
      }
    },

    calculateTotals() {
      const price = this.convertDecimalNumberII(this.data.supply_price_day || 0);
      const liters = this.convertDecimalNumberII(this.data.supply_liters_stocked || 0);
      const biddingPrice = this.convertDecimalNumberII(this.data.supply_bidding_price || 0);
      
      this.data.supply_value = (price * liters).toFixed(2);
      this.data.total_licitado = (biddingPrice * liters).toFixed(2);
    },

    calculateSubTotals() {
      const price = this.convertDecimalNumberII(this.dataAdjust.supply_price_day || 0);
      const liters = this.convertDecimalNumberII(this.dataAdjust.supply_liters_stocked || 0);
      this.dataAdjust.supply_value = (price * liters).toFixed(2);
    },

    checkSupply(id) {
      if (!confirm("Confirmar o abastecimento com os dados atuais?")) return;
      this.$store.dispatch("setLoader", true);
      this.$http
        .put(`/supplies/single/${id}`, { supply_id: id, supply_confered: true, supply_status: "CONCLUIDO" })
        .then(() => {
          this.showSnack("Conferido com sucesso!", "success");
          this.$store.dispatch("setLoader", false);
          this.search();
        })
        .catch((e) => {
          this.showSnack(e.message, "error");
          this.$store.dispatch("setLoader", false);
        });
    },

    getSupplyByUpdate(id) {
      this.$http.get(`/supplies/single/${id}`).then((r) => {
        this.dataAdjust = r.data[0];
        this.dataAdjust.supply_liters_stocked = parseFloat(r.data[0].supply_liters_stocked || 0).toFixed(3);
        this.dataAdjust.supply_price_day = parseFloat(r.data[0].supply_price_day || 0).toFixed(3);
        this.dataAdjust.datehour = this.convertDateTime(r.data[0].datehour);
        this.showForm = true;
      }).catch(() => {
        this.showSnack("Erro ao carregar os dados para edição", "error");
      });
    },

    ajust() {
      this.loader = true;
      this.dataAdjust.supply_adjusted = true;
      this.dataAdjust.supply_status = "CONCLUIDO";
      this.$http
        .put(`/supplies/single/${this.dataAdjust.supply_id}`, this.dataAdjust)
        .then((r) => {
          this.showSnack(r.data.result || "Ajustado com sucesso", "success");
          setTimeout(() => { this.showForm = false; this.loader = false; this.search(); }, 1500);
        })
        .catch((e) => { this.showSnack(e.message, "error"); this.loader = false; });
    },

    validate() {
      if (!this.$refs.form.validate()) { this.showSnack("Preencha todos os campos obrigatórios", "warning"); return; }
      if (!this.data.vehicle_id && !this.data.supply_extra) { this.showSnack("É preciso marcar como correção se não houver veículo!", "warning"); return; }
      this.createOrUpdate();
    },

    createOrUpdate() {
      this.loader = true;
      this.data.provider_id = this.userGroup == 1 ? this.filter.provider_id : this.$store.getters.getUserData.table_id;
      this.data.supply_status = "CONCLUIDO";
      this.$store.dispatch("setLoader", true);
      this.$http.post("/supplies/single", this.data).then((r) => {
          this.showSnack(r.data.result ? r.data.result : r.data.warn, r.data.result ? "success" : "error");
          this.loader = false;
          this.$store.dispatch("setLoader", false);
          if (r.data.result) this.resetForm();
        }).catch((e) => { this.showSnack(e.message, "error"); this.loader = false; this.$store.dispatch("setLoader", false); });
    },

    resetForm() {
       this.data = { 
         supply_id: null, 
         supply_price_day: 0, 
         datehour: "", 
         supply_bidding_price: 0, 
         supply_adjusted: false, 
         supply_liters_stocked: 0, 
         item_id: null, 
         vehicle_id: null, 
         driver_id: null,
         sec_id: null,
         subsec_id: null,
         justificativa: null,
         supply_status: "CONCLUIDO", 
         supply_extra: false 
       };
    },

    showSnack(message, color) { this.snack.show = true; this.snack.message = message; this.snack.color = color; },

    printBillingReport(item) {
      // Usar a rota com secretarias (billingSecs) quando não tiver vehicle_id
      const vId = item.vehicle_id || 'null';
      let url = '';
      if (item.sec_id) {
         url = `/billings/report/single/${this.filter.startDate}/${this.filter.endDate}/${this.filter.prefecture_id}/${this.filter.provider_id}/${vId}/${item.sec_id}`;
      } else {
         url = `/billings/report/single/${this.filter.startDate}/${this.filter.endDate}/${this.filter.prefecture_id}/${this.filter.provider_id}/${vId}`;
      }
      
      const routeData = this.$router.resolve({ path: url });
      window.open(routeData.href, '_blank');
    }
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

.shadow-custom {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.02);
}

.custom-select >>> .v-input__control {
  min-height: 44px !important;
}

.custom-select >>> .v-input__slot {
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  padding: 0 12px !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
}

.custom-field-form >>> .v-input__slot {
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 10px !important;
  padding: 0 16px !important;
  min-height: 48px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
}

.custom-table {
  border: none !important;
}

.custom-table >>> th {
  background-color: #f8fafc !important;
  color: #64748b !important;
  font-size: 0.65rem !important;
  font-weight: 800 !important;
  letter-spacing: 0.05em !important;
  border-bottom: 1px solid #e2e8f0 !important;
  height: 48px !important;
  text-transform: uppercase !important;
}

.custom-table >>> td {
  border-bottom: 1px solid #f1f5f9 !important;
  padding: 16px !important;
  font-size: 0.825rem !important;
  color: #334155 !important;
}

.custom-table >>> tr:hover {
  background-color: #f8fafc !important;
}

button:focus { outline: none; }
</style>